/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * "Check readiness" on a transfer's page: the same readiness report as the wizard's step, for the
 * transfer's VMs, target, options and network map as they are now (for example before a cut-over).
 */

import * as React from "react";
import { Stethoscope } from "lucide-react";

import { ActionButton } from "@src/components/ui/DetailsKit";
import { FootNote, OpsDialog } from "@src/components/ui/OpsDialog";
import cutoverSource, { apiErrorMessage } from "@src/sources/CutoverSource";

import type { ReadinessReport, ReadinessRequest } from "@src/@types/Cutover";
import type { TransferItemDetails } from "@src/@types/MainItem";

import ReadinessReportView from "./ReadinessReport";

export const transferReadinessRequest = (
  transfer: TransferItemDetails,
  opts: { linkMbps?: string; refresh?: boolean } = {},
): ReadinessRequest => {
  const body: ReadinessRequest = {
    instances: transfer.instances || [],
    target_endpoint_id: transfer.destination_endpoint_id,
    source_options: transfer.source_environment || {},
    target_options: transfer.destination_environment || {},
    timeout_seconds: 45,
  };
  if (transfer.network_map && typeof transfer.network_map === "object") body.network_map = transfer.network_map as any;
  const link = Number(opts.linkMbps);
  if (opts.linkMbps && link > 0) body.link_mbps = link;
  if (opts.refresh) body.refresh = true;
  return body;
};

type Props = {
  transfer: TransferItemDetails;
  targetName?: string | null;
  onClose: () => void;
};

const TransferReadinessDialog = ({ transfer, targetName, onClose }: Props) => {
  const [report, setReport] = React.useState<ReadinessReport | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [linkMbps, setLinkMbps] = React.useState("");
  const alive = React.useRef(true);

  const run = async (refresh: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const r = await cutoverSource.readiness(transfer.origin_endpoint_id, transferReadinessRequest(transfer, { linkMbps, refresh }));
      if (alive.current) setReport(r);
    } catch (err: any) {
      if (alive.current) setError(apiErrorMessage(err, "SwiftIQ could not check the VMs."));
    } finally {
      if (alive.current) setLoading(false);
    }
  };

  React.useEffect(() => {
    run(false);
    return () => {
      alive.current = false;
    };
  }, [transfer.id]);

  return (
    <OpsDialog
      title="Readiness report"
      subtitle={`${transfer.notes || transfer.instances?.[0]} → ${targetName || "target"} · read-only, nothing is changed`}
      icon={Stethoscope}
      width={1040}
      onRequestClose={onClose}
      testId="transferReadinessDialog"
      footer={
        <>
          <FootNote>To fix a check, edit the transfer's options or network map, then run the report again.</FootNote>
          <ActionButton type="button" variant="secondary" onClick={onClose}>
            Close
          </ActionButton>
        </>
      }
    >
      <ReadinessReportView
        report={report}
        loading={loading}
        error={error}
        linkMbps={linkMbps}
        onLinkMbpsChange={setLinkMbps}
        onRefresh={() => run(true)}
        networksHint="in the transfer's Network Mapping (Edit)"
        instanceCount={transfer.instances?.length}
      />
    </OpsDialog>
  );
};

export default TransferReadinessDialog;
